import { PlatformError } from '@djgg/core';
import type { CreationFilmRunBindingV1 } from './creationFilms.js';
import type { ConceptAnalysisDraft } from './types.js';

/** How far consuming a proposal moves the draft's revision. The composition
 * review triggers (migrations 0122/0126) assert the same step in SQL. */
const CONSUMPTION_REVISION_STEP = 1;

/**
 * The ONE way a reviewed proposal becomes consumed by the version it built.
 * Consuming moves the draft's revision on by exactly one step, so stale
 * reloads of the proposal lose; `analysisFilmBindingV1` inverts that step, so
 * the film collected while the proposal was prepared stays the film that seals
 * onto `consumedVersion`.
 */
export function consumeConceptAnalysisProposal(
  draft: ConceptAnalysisDraft,
  consumedVersion: number,
  at: number,
): ConceptAnalysisDraft {
  if (draft.status !== 'proposal') throw new PlatformError('conflict', 'only a proposal can be consumed');
  return {
    ...draft,
    revision: draft.revision + CONSUMPTION_REVISION_STEP,
    status: 'consumed',
    consumedVersion,
    updatedAt: at,
  };
}

/**
 * The analysis-stage film identity of a draft: the proposal revision, which a
 * consumed draft carries one consumption step back. Every film reader and
 * writer derives an analysis binding here, never from `draft.revision`.
 */
export function analysisFilmBindingV1(draft: ConceptAnalysisDraft): Extract<CreationFilmRunBindingV1, { kind: 'analysis' }> {
  const revision = draft.status === 'consumed' ? draft.revision - CONSUMPTION_REVISION_STEP : draft.revision;
  return { kind: 'analysis', operationId: draft.operationId, revision };
}

/** True when the draft still holds this analysis film authority. */
export function analysisDraftHoldsFilmBinding(
  draft: ConceptAnalysisDraft | undefined,
  binding: Extract<CreationFilmRunBindingV1, { kind: 'analysis' }>,
): draft is ConceptAnalysisDraft {
  return draft !== undefined && draft.operationId === binding.operationId
    && analysisFilmBindingV1(draft).revision === binding.revision;
}
